import * as Sentry from "@sentry/vue";
import { FullStory, init } from "@fullstory/browser";

import type { SnippetOptions } from "@fullstory/browser";
import type { AnalyticsProvider } from "../types";

import {
  AnalyticsProviderApiKeyMissingError,
  AnalyticsProviderInitializationError,
} from "../exceptions";

export interface FullStoryProviderConfig {
  orgId?: string;
  environment?: string;
  options?: Partial<SnippetOptions>;
}

export class FullStoryAnalyticsProvider implements AnalyticsProvider {
  constructor(private readonly config: FullStoryProviderConfig = {}) {}

  init() {
    const orgId = this.config.orgId || import.meta.env.VITE_FS_ORG_ID;

    if (!orgId?.length) {
      console.error(new AnalyticsProviderApiKeyMissingError(FullStoryAnalyticsProvider.name));
      return false;
    }

    try {
      init({
        orgId,
        recordCrossDomainIFrames: true,
        devMode: false,
        ...this.config.options,
      });
      return true;
    } catch (error) {
      Sentry.captureException(
        new AnalyticsProviderInitializationError(FullStoryAnalyticsProvider.name, error as Error)
      );
      return false;
    }
  }

  trackEvent(eventName: string, properties?: Record<string, unknown>) {
    const environment = this.config.environment || import.meta.env.MODE;

    FullStory("trackEvent", {
      name: eventName,
      properties: { environment, ...properties },
    });
  }

  onUserChange<TUserData extends { id: string | number }>(userData: TUserData | null) {
    const identityPayload = userData?.id
      ? { uid: String(userData.id), properties: userData }
      : { anonymous: true };

    FullStory("restart");
    FullStory("setIdentity", identityPayload);
  }

  onUserPropertiesChange<TData extends Record<string, unknown>>(properties: TData) {
    FullStory("setProperties", { type: "user", properties });
  }
}
